import React, { useState, useEffect, useRef } from 'react';
import { Button, Radio, Label } from 'flowbite-react';
import Datepicker, { DateValueType } from 'react-tailwindcss-datepicker';
import { DateFilterType, DateRange } from '../../pages/UserDashboard';
import moment from 'moment';
import { BsChevronDown } from 'react-icons/bs';
import { HiOutlineAdjustmentsHorizontal } from 'react-icons/hi2';

interface DateFilterProps {
    selectedFilter: DateFilterType;
    onFilterChange: (filter: DateFilterType) => void;
    customDateRange: DateRange;
    onCustomDateRangeChange: (range: DateRange) => void;
    onApply: () => void;
}

const DateFilter: React.FC<DateFilterProps> = ({
    selectedFilter,
    onFilterChange,
    customDateRange,
    onCustomDateRangeChange,
    onApply,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempFilter, setTempFilter] = useState<DateFilterType>(selectedFilter);
    const [tempCustomDateRange, setTempCustomDateRange] = useState<DateRange>(customDateRange);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                // Reset temp values on close
                setTempFilter(selectedFilter);
                setTempCustomDateRange(customDateRange);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, selectedFilter, customDateRange]);

    const handleDatePickerChange = (value: DateValueType) => {
        if (value) {
            setTempCustomDateRange({
                startDate: value.startDate ? moment(value.startDate).toDate() : null,
                endDate: value.endDate ? moment(value.endDate).toDate() : null,
            });
        }
    };

    const filterOptions: { label: string; value: DateFilterType }[] = [
        { label: 'Overall', value: 'overall' },
        { label: '7 Days', value: '7days' },
        { label: '30 Days', value: '30days' },
        { label: 'Customised', value: 'custom' },
    ];

    const handleApply = () => {
        onFilterChange(tempFilter);
        if (tempFilter === 'custom') {
            onCustomDateRangeChange(tempCustomDateRange);
        }
        onApply();
        setIsOpen(false);
    };

    const handleCancel = () => {
        setTempFilter(selectedFilter);
        setTempCustomDateRange(customDateRange);
        setIsOpen(false);
    };

    const getFilterDisplayText = (): string => {
        if (selectedFilter === 'overall') {
            return 'Filter';
        }
        const selectedOption = filterOptions.find(opt => opt.value === selectedFilter);
        if (selectedOption) {
            if (selectedFilter === 'custom' && customDateRange.startDate && customDateRange.endDate) {
                // Show date range for custom filter
                const start = moment(customDateRange.startDate).format('DD/MM/YYYY');
                const end = moment(customDateRange.endDate).format('DD/MM/YYYY');
                return `${start} - ${end}`;
            }
            return selectedOption.label;
        }
        return 'Filter';
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <Button
                color="light"
                className="bg-white dark:bg-[#1F2A37] border border-[#d1d5db] dark:border-[#555770] text-[#28293D] dark:text-white font-inter text-sm font-medium px-4 py-2 hover:bg-gray-50 dark:hover:bg-[#2a3441]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <HiOutlineAdjustmentsHorizontal className="w-4 h-4 mr-2" />
                <span className="mr-2">{getFilterDisplayText()}</span>
                <BsChevronDown className="w-4 h-4" />
            </Button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 min-w-[320px] bg-white dark:bg-[#1F2A37] border border-[#d1d5db] dark:border-[#555770] rounded-lg shadow-lg z-50">
                    <div className="p-4 space-y-4">
                        <fieldset className="space-y-3">
                            {filterOptions.map(option => (
                                <div key={option.value} className="flex items-center gap-2">
                                    <Radio
                                        id={option.value}
                                        name="dateFilter"
                                        value={option.value}
                                        checked={tempFilter === option.value}
                                        onChange={() => setTempFilter(option.value)}
                                        className="text-[#7777E7] focus:ring-[#7777E7]"
                                    />
                                    <Label htmlFor={option.value} className="font-inter text-sm text-[#28293D] dark:text-white cursor-pointer">
                                        {option.label}
                                    </Label>
                                </div>
                            ))}
                        </fieldset>

                        {tempFilter === 'custom' && (
                            <div className="mt-4 pt-4 border-t border-[#d1d5db] dark:border-[#555770]">
                                <Datepicker
                                    useRange={true}
                                    value={{
                                        startDate: tempCustomDateRange.startDate,
                                        endDate: tempCustomDateRange.endDate,
                                    }}
                                    onChange={handleDatePickerChange}
                                    inputClassName="w-full rounded-md border border-[#d1d5db] dark:border-[#555770] font-inter focus:ring-0 focus:border-[#7777E7] font-normal bg-white dark:bg-[#1F2A37] placeholder:text-[#555770] dark:placeholder:text-[#E4E4EB] text-[#28293D] dark:text-white"
                                    placeholder="Select a date range"
                                    displayFormat="DD/MM/YYYY"
                                />
                            </div>
                        )}

                        <div className="flex gap-2 pt-2">
                            <Button
                                color="light"
                                onClick={handleCancel}
                                className="flex-1 font-inter text-sm"
                            >
                                Cancel
                            </Button>
                            <Button
                                color="purplePlaced"
                                onClick={handleApply}
                                className="flex-1 bg-[#7777E7] font-inter text-sm"
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateFilter;

